/**
 * SMSDialogs - Confirmation and Template dialogs
 */
import { AlertTriangle, Send, RefreshCw, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CAMPAIGN_NAMES, TEMPLATE_CATEGORIES, calculateMessageStats } from './smsUtils';

export function SMSConfirmDialog({
  open,
  onOpenChange,
  previewData,
  recipientType,
  phoneNumber,
  campaignId,
  message,
  sending,
  onSend
}) {
  const { charCount, segmentCount } = calculateMessageStats(message);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Confirm SMS Broadcast
          </DialogTitle>
          <DialogDescription>
            Please review before sending
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Recipient Count */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
            <p className="text-lg font-bold text-amber-700 dark:text-amber-400">
              {previewData?.count || 0} recipient{previewData?.count !== 1 ? 's' : ''}
            </p>
            <p className="text-sm text-amber-600 dark:text-amber-500">
              {recipientType === 'individual' 
                ? `Sending to: ${phoneNumber}`
                : recipientType === 'campaign'
                ? `Campaign: ${campaignId === 'all' ? 'All Campaigns' : CAMPAIGN_NAMES[campaignId]}`
                : recipientType === 'loyalty'
                ? 'All Loyalty Members'
                : 'All Subscribers'
              }
            </p>
          </div>

          {/* Message Preview */}
          <div className="space-y-2">
            <Label className="text-foreground">Message Preview</Label>
            <div className="bg-muted rounded-lg p-4">
              <p className="text-sm text-foreground whitespace-pre-wrap">{message}</p>
            </div>
            <p className="text-xs text-muted-foreground">
              {charCount} characters - {segmentCount} SMS segment{segmentCount !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Sample Recipients */}
          {previewData?.sample && previewData.sample.length > 0 && (
            <div className="space-y-2">
              <Label className="text-foreground">Sample Recipients</Label>
              <div className="bg-muted rounded-lg p-3 max-h-32 overflow-y-auto">
                {previewData.sample.map((r, i) => (
                  <div key={i} className="text-sm text-foreground py-1">
                    {r.name} - {r.phone} {r.country_name && `(${r.country_name})`}
                  </div>
                ))}
                {previewData.count > 10 && (
                  <p className="text-xs text-muted-foreground pt-2">
                    ...and {previewData.count - 10} more
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Cost Warning */}
          {previewData?.count > 1 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <p className="text-sm text-blue-700 dark:text-blue-400">
                <strong>Note:</strong> Sending {previewData.count} messages x {segmentCount} segment{segmentCount !== 1 ? 's' : ''} = ~{previewData.count * segmentCount} SMS segments
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={onSend} 
            disabled={sending}
            className="bg-primary"
            data-testid="sms-confirm-send-btn"
          >
            {sending ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send {previewData?.count || 0} SMS
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SMSTemplateDialog({
  open,
  onOpenChange,
  editingTemplate,
  templateForm,
  setTemplateForm,
  onSave
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-foreground">
            {editingTemplate ? 'Edit Template' : 'Create Template'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label className="text-foreground">Template Name</Label>
            <Input
              value={templateForm.name}
              onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Grand Opening Announcement"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-foreground">Category</Label>
            <Select 
              value={templateForm.category} 
              onValueChange={(value) => setTemplateForm(prev => ({ ...prev, category: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TEMPLATE_CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-foreground">Message Content</Label>
            <Textarea
              value={templateForm.content}
              onChange={(e) => setTemplateForm(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Type your template message..."
              rows={4}
            />
            <p className="text-xs text-muted-foreground">
              Use {`{{name}}`}, {`{{first_name}}`}, {`{{coupon_code}}`} for personalization
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave}>
            <Save className="w-4 h-4 mr-2" />
            {editingTemplate ? 'Update' : 'Save'} Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
